import { ReactElement } from "react";
import WorkflowStep from "./WorkflowStep";
import { getWorkflowConfiguration } from "../config/workflow.config";

export interface WorkflowStepsProps {
  dataType: string;
  className?: string;
  onStepSelect?: (stepId: string) => void;
  completedSteps?: string[];
  activeStep?: string;
}

const WorkflowSteps = ({
  dataType,
  className = "",
  onStepSelect,
  completedSteps = [],
  activeStep,
}: WorkflowStepsProps): ReactElement => {
  const workflowConfig = getWorkflowConfiguration(dataType);
  const steps = workflowConfig.steps;

  const handleStepClick = (stepId: string) => {
    // Allow clicking any step that's not disabled
    if (!isStepDisabled(stepId)) {
      onStepSelect?.(stepId);
    }
  };

  const isStepDisabled = (stepId: string) => {
    const stepIndex = workflowConfig.stepOrder.indexOf(stepId);
    if (stepIndex === -1) return true;

    // Quality Control is always enabled
    if (stepId === "quality-control") return false;

    // For single-cell data, handle special cases
    if (dataType === "single-cell") {
      // Filter Cells & Genes requires Quality Control
      if (stepId === "filter-cells-genes") {
        return !completedSteps.includes("quality-control");
      }

      // Doublet Detection requires Filter Cells & Genes
      // But if normalization is completed, doublet detection becomes disabled (skipped)
      // if (stepId === "doublet-detection") {
      //   if (completedSteps.includes("normalization")) {
      //     return true; // Disabled if normalization is completed (was skipped)
      //   }
      //   return !completedSteps.includes("filter-cells-genes");
      // }

      // Normalization can be accessed after filter-cells-genes OR doublet-detection
      if (stepId === "normalization") {
        return (
          !completedSteps.includes("filter-cells-genes")
          // !completedSteps.includes("filter-cells-genes") &&
          // // !completedSteps.includes("doublet-detection")
        );
      }

      // Post-clustering steps are sequential in the guided workflow.
      if (stepId === "cell-type-annotation") {
        return !completedSteps.includes("clustering");
      }
      if (stepId === "marker-gene-set") {
        return !completedSteps.includes("cell-type-annotation");
      }
      if (stepId === "de-genes-markers") {
        return !completedSteps.includes("marker-gene-set");
      }

      // Post-clustering DEPENDENT steps: require specific previous steps
      // Enrichment requires DE Genes as Markers to be completed
      if (stepId === "enrichment") {
        return !completedSteps.includes("de-genes-markers");
      }

      // Trajectory is the final guided step.
      if (stepId === "trajectory-analysis") {
        return !completedSteps.includes("cell-type-annotation");
      }
    }

    // For all other steps, require the previous step to be completed
    if (stepIndex > 0) {
      const previousStep = workflowConfig.stepOrder[stepIndex - 1];
      return !completedSteps.includes(previousStep);
    }

    return false;
  };

  const getStepStatus = (
    stepId: string,
  ): "unselected" | "selected" | "completed" | "disabled" => {
    const isDisabled = isStepDisabled(stepId);
    const isActive = activeStep === stepId;
    const isCompleted = completedSteps.includes(stepId);

    if (isCompleted) return "completed";
    if (isActive) return "selected";
    if (isDisabled) return "disabled";
    return "unselected";
  };

  // Group steps for better visual organization
  const preClusteringSteps = steps.filter((step) =>
    [
      "quality-control",
      "filter-cells-genes",
      // "doublet-detection",
      "normalization",
      "feature-selection",
      "dimensionality-reduction",
      "umap",
      "clustering",
    ].includes(step.id),
  );

  const postClusteringIndependentSteps = steps.filter((step) =>
    ["cell-type-annotation", "marker-gene-set", "de-genes-markers"].includes(
      step.id,
    ),
  );

  const downstreamSteps = steps.filter((step) =>
    ["trajectory-analysis"].includes(step.id),
  );

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Pre-clustering steps */}
      <div className="space-y-3">
        {preClusteringSteps.map((step) => (
          <WorkflowStep
            key={step.id}
            title={step.title}
            description={step.description}
            status={getStepStatus(step.id)}
            onClick={() => handleStepClick(step.id)}
          />
        ))}
      </div>

      {/* Post-clustering independent steps with clear visual branching */}
      {postClusteringIndependentSteps.length > 0 && (
        <div className="relative">
          {/* Main vertical connector from clustering, extending through all independent steps */}
          <div className="absolute left-4 top-0 w-0.5 bg-blue-400 h-full"></div>

          <div className="ml-8 space-y-6">
            {postClusteringIndependentSteps.map((step) => (
              <div key={step.id} className="relative">
                {/* Horizontal connector from main vertical line to this step */}
                <div className="absolute -left-4 top-6 w-4 h-0.5 bg-blue-400"></div>

                <WorkflowStep
                  title={step.title}
                  description={step.description}
                  status={getStepStatus(step.id)}
                  onClick={() => handleStepClick(step.id)}
                />

                {/* Add enrichment directly under DE Genes as Markers */}
                {step.id === "de-genes-markers" && (
                  <div className="mt-3">
                    <WorkflowStep
                      title="Biological Pathways & Processes"
                      description="Discover biological pathways, processes, and functions associated with your selected genes."
                      status={getStepStatus("enrichment")}
                      onClick={() => handleStepClick("enrichment")}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Downstream trajectory step */}
      {downstreamSteps.length > 0 && (
        <div className="mt-6 space-y-3">
          {downstreamSteps.map((step) => (
            <WorkflowStep
              key={step.id}
              title={step.title}
              description={step.description}
              status={getStepStatus(step.id)}
              onClick={() => handleStepClick(step.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default WorkflowSteps;
